import { useTranslation } from 'react-i18next'

import {
  useDateLocale,
  useNotification,
  useTimeZone,
  useUserData,
} from '../../../../../hooks'
import { getIsCalendarGridDayFull } from '../../../../CalendarGrid'
import {
  getMockCalendarRequestsDayLoad,
  moveMockCalendarFollowUps,
} from '../../../Calendar.mock'
import { formatCalendarDate } from '../../../Calendar.utils'
import { CalendarFollowUpCapModal } from '../../../CalendarFollowUpCapModal'
import { getIsCalendarViewItemChangedMeanwhile } from '../../CalendarView.utils'

import {
  getCalendarViewFollowUpCap,
  getCalendarViewFollowUpUncheckedMoveText,
} from './useCalendarViewFollowUpMove.utils'

export function useCalendarViewFollowUpMove({
  getActionToken,
  setModal,
  onClose,
  onReload,
}) {
  const { t } = useTranslation()

  const locale = useDateLocale()
  const timezone = useTimeZone()
  const userData = useUserData()

  const {
    handleAddErrorNotification,
    handleAddSuccessNotification,
    handleAddWarningNotification,
  } = useNotification()

  // TODO: write the due date through the request's due date update, keeping
  // its time of day, once the backend returns the target day's load with it
  // (TM-01-13).
  async function moveFollowUp({ date, item }) {
    return moveMockCalendarFollowUps({
      date,
      ids: [item.id],
      sourceDate: item.date,
      timezone,
    })
  }

  // TODO: read the day load of the request's assignees from the day-load
  // endpoint once the backend serves it (TM-01-12).
  async function loadFollowUpCap({ date, item }) {
    return getMockCalendarRequestsDayLoad({
      date,
      requestIds: [item.requestId],
      timezone,
      user: userData,
    })
      .then(({ data }) => ({
        cap: getCalendarViewFollowUpCap({ data, date, user: userData }),
        isChecked: true,
      }))
      .catch(() => ({ cap: null, isChecked: false }))
  }

  function reloadMovedFollowUp({ date, item }) {
    onReload().then(items => {
      if (getIsCalendarViewItemChangedMeanwhile({ date, item, items })) {
        handleAddWarningNotification('Calendar.EntryChangedMeanwhileKey')
      }
    })
  }

  function handleFollowUpMoved({ date, item }) {
    reloadMovedFollowUp({ date, item })

    handleAddSuccessNotification({
      title: t('Calendar.FollowUpMovedToastKey', {
        date: formatCalendarDate({ date, locale }),
      }),
    })
  }

  async function handleMoveFollowUpTo({ date, item }) {
    return moveFollowUp({ date, item })
      .then(() => {
        handleFollowUpMoved({ date, item })
      })
      .catch(() => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
  }

  async function handleMoveFollowUpFromCap({ date, item }) {
    const actionToken = getActionToken()

    return moveFollowUp({ date, item })
      .then(() => {
        if (getActionToken() === actionToken) {
          onClose()
        }

        handleFollowUpMoved({ date, item })
      })
      .catch(() => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
  }

  async function handleMoveFollowUpUnchecked({ date, item }) {
    return moveFollowUp({ date, item })
      .then(({ data }) => {
        reloadMovedFollowUp({ date, item })

        handleAddWarningNotification({
          text: getCalendarViewFollowUpUncheckedMoveText({
            dayLoad: data.day_load,
            locale,
            t,
          }),
          title: t('Calendar.FollowUpMovedToastKey', {
            date: formatCalendarDate({ date, locale }),
          }),
        })
      })
      .catch(() => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
  }

  async function handleMoveFollowUpDirectly({ date, isChecked, item }) {
    return isChecked
      ? handleMoveFollowUpTo({ date, item })
      : handleMoveFollowUpUnchecked({ date, item })
  }

  function handleShowFollowUpCap({ cap, date, item }) {
    setModal(
      <CalendarFollowUpCapModal
        cap={cap}
        date={date}
        item={item}
        onClose={onClose}
        onMove={handleMoveFollowUpFromCap}
      />
    )
  }

  function handleFollowUpCapLoaded({ cap, date, isChecked, item }) {
    if (cap) {
      handleShowFollowUpCap({ cap, date, item })
    } else {
      onClose()

      handleMoveFollowUpDirectly({ date, isChecked, item })
    }
  }

  async function handleOpenFollowUpCap({ date, item }) {
    const actionToken = getActionToken()

    handleShowFollowUpCap({ cap: null, date, item })

    return loadFollowUpCap({ date, item }).then(result => {
      if (getActionToken() === actionToken) {
        handleFollowUpCapLoaded({ ...result, date, item })
      }
    })
  }

  async function handleCheckFollowUpMove({ date, item }) {
    const actionToken = getActionToken()

    return loadFollowUpCap({ date, item }).then(({ cap, isChecked }) => {
      if (!cap) {
        handleMoveFollowUpDirectly({ date, isChecked, item })
      } else if (getActionToken() === actionToken) {
        handleShowFollowUpCap({ cap, date, item })
      }
    })
  }

  async function handleMoveFollowUp({ date, dayLoad, item }) {
    return getIsCalendarGridDayFull(dayLoad)
      ? handleOpenFollowUpCap({ date, item })
      : handleCheckFollowUpMove({ date, item })
  }

  return { handleMoveFollowUp }
}
